import { oc } from "@orpc/contract";
import { z } from "zod";

// ── Response schemas ──

export const ResourceTagSchema = z.object({
  id: z.string().uuid(),
  tagDefinitionId: z.string().uuid(),
  resourceType: z.string(),
  resourceId: z.string().uuid(),
  /** Human-readable name of the tagged resource; null if it can't be resolved. */
  resourceLabel: z.string().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});
export type ResourceTagDTO = z.infer<typeof ResourceTagSchema>;

// ── Input schemas ──

/**
 * Lookup-by-resource: pass `{ resourceType, resourceId }` to list every tag
 * on a single resource. Lookup-by-tag: pass `{ tagDefinitionId }` to list
 * every resource carrying a given tag. At least one selector must be present.
 */
export const ListResourceTagsInputSchema = z
  .object({
    resourceType: z.string().min(1).optional(),
    resourceId: z.string().uuid().optional(),
    tagDefinitionId: z.string().uuid().optional(),
  })
  .refine(
    (v) =>
      v.tagDefinitionId !== undefined ||
      (v.resourceType !== undefined && v.resourceId !== undefined),
    {
      message:
        "Provide either tagDefinitionId or (resourceType AND resourceId)",
    },
  );
export type ListResourceTagsInput = z.infer<typeof ListResourceTagsInputSchema>;

export const ListResourceTagsResponseSchema = z.object({
  tags: z.array(ResourceTagSchema),
});

export const AddResourceTagInputSchema = z.object({
  tagDefinitionId: z.string().uuid(),
  resourceType: z.string().min(1),
  resourceId: z.string().uuid(),
});
export type AddResourceTagInput = z.infer<typeof AddResourceTagInputSchema>;

/**
 * Remove by row id. The architecture doc flags tag mutation as privileged —
 * an alternative composite-key remove (`{ tagDefinitionId, resourceType,
 * resourceId }`) is intentionally NOT exposed in phase 1: callers that need
 * idempotent "untag this resource" semantics should first list-by-resource
 * to obtain the row id. Keeping a single-shape `remove` keeps the audit
 * trail (one row-id per call) unambiguous.
 */
/**
 * Repoint an existing tag row to a different resource, keeping the same row id
 * and tag definition. Editing a resource tag is conceptually "move this tag to
 * another resource" — equivalent to remove + add, but atomic and audit-stable.
 */
export const UpdateResourceTagInputSchema = z.object({
  id: z.string().uuid(),
  resourceType: z.string().min(1),
  resourceId: z.string().uuid(),
});
export type UpdateResourceTagInput = z.infer<
  typeof UpdateResourceTagInputSchema
>;

export const RemoveResourceTagInputSchema = z.object({
  id: z.string().uuid(),
});
export type RemoveResourceTagInput = z.infer<
  typeof RemoveResourceTagInputSchema
>;

export const RemoveResourceTagResponseSchema = z.object({
  removed: z.boolean(),
});

// ── Contract ──
//
// TODO(authz): see tag-definitions.contract.ts — phase 1 reuses
// Subject.ResourcePrivilege for tag-write authorization. Re-evaluate if a
// finer-grained Subject.ResourceTag lands.
export const resourceTagsContracts = {
  list: oc
    .input(ListResourceTagsInputSchema)
    .output(ListResourceTagsResponseSchema)
    .route({ method: "GET" }),
  add: oc
    .input(AddResourceTagInputSchema)
    .output(ResourceTagSchema)
    .route({ method: "POST" }),
  update: oc
    .input(UpdateResourceTagInputSchema)
    .output(ResourceTagSchema)
    .route({ method: "POST" }),
  remove: oc
    .input(RemoveResourceTagInputSchema)
    .output(RemoveResourceTagResponseSchema)
    .route({ method: "POST" }),
} as const;
